import type { SungRange } from '../capture';

/**
 * pitchWindow — 부른 음높이를 화면 세로 위치로 옮기는 순수 계산.
 *
 * **절대 음높이를 표시하지 않는 화면의 축**이다. 건반도 눈금도 없이 "지금 어디쯤"만
 * 보여주려면 창이 흔들리지 않아야 하는데, 그 안정성은 두 규칙이 만든다:
 *
 * 1. 창 폭은 **옥타브 단위로만** 자란다. 부른 폭을 그대로 창으로 쓰면 음이 하나 들어올
 *    때마다 반음 높이가 달라져, 같은 떨림이 커졌다 작아졌다 한다. 올림한 옥타브가 곧
 *    창이다 — 거기에 여유를 한 옥타브 더 얹지 않는다.
 * 2. 창 중심은 **6반음 격자에 붙는다**. 프레임마다 중심을 다시 잡으면 축이 계속
 *    미끄러져, 화면만 봐서는 내가 움직인 건지 축이 움직인 건지 알 수 없다.
 *
 * 둘 다 이미 한 번 고친 버그의 해답이다 — 화면을 새로 짜도 이 계산은 그대로 간다.
 *
 * React·DOM 의존 없음 — vitest 대상.
 */

export interface MidiWindow {
  lo: number;
  hi: number;
}

/** 최소 2옥타브 — 더 좁으면 미세한 떨림이 산맥처럼 과장된다. */
export const MIN_SPAN = 24;

/**
 * 최대 3옥타브. **이 상한이 없으면 오검출 한 프레임이 화면을 통째로 가져간다.**
 *
 * 창을 넓히는 근거(`sung`)는 "그린 프레임 전부"인데, 그 안에는 노래가 아닌 유성 프레임이
 * 섞인다 — 실측(`.claude/20260805/DEBUG-15-49-40.md`)에서 시작 직후 럼블 38.5와 옥타브
 * 오검출 84.3이 창의 양 끝을 잡아 창이 60반음(5옥타브)으로 벌어졌고, 실제로 부른 폭은
 * 화면 높이의 25%로 눌렸다.
 *
 * 3옥타브인 이유: 저음·고음은 **세그먼트가 따로**라(`recordTests`) 한 테이크가 담는 건
 * 편한 음에서 한쪽 방향 스윕뿐이고, 그건 3옥타브를 안 넘는다. 상한에 걸린 프레임은
 * `yOfWindow`가 가장자리로 물리므로 사라지지 않는다 — 눌려서 아무것도 안 읽히는 것보다
 * 가장자리에 붙는 편이 낫다.
 */
export const MAX_SPAN = 36;

/** 창 중심을 붙일 격자(반음) — 프레임마다 미끄러지지 않게. */
export const CENTER_SNAP = 6;

/**
 * 부른 폭과 창 가장자리 사이에 남기는 최소 여백(반음). 0이면 가장 높은 음이 화면 맨 위
 * 선에 붙어 잘린 것처럼 보인다 — 한 반음이면 그 음이 제 높이로 그려진다.
 */
export const EDGE_MARGIN = 1;

/** 소리 나기 전 기준(C3~C4 한가운데). */
export const BASE_CENTER = 54;

const merge = (a: SungRange | null, b: SungRange | null): SungRange | null => {
  if (a == null) {
    return b;
  }
  if (b == null) {
    return a;
  }
  return { lo: Math.min(a.lo, b.lo), hi: Math.max(a.hi, b.hi) };
};

const fits = (range: SungRange, center: number, span: number): boolean =>
  center - span / 2 <= range.lo - EDGE_MARGIN &&
  center + span / 2 >= range.hi + EDGE_MARGIN;

/**
 * 창 중심 — 격자에 붙이되 부른 폭이 창 밖으로 밀리지 않는 선까지만 되돌린다.
 * 여유가 아예 없으면 격자 값을 그대로 돌려준다(호출자가 한 계단 넓혀 다시 묻는다).
 */
const centerFor = (range: SungRange, span: number): number => {
  const middle = (range.lo + range.hi) / 2;
  const snapped = CENTER_SNAP * Math.round(middle / CENTER_SNAP);
  const lowest = range.hi + EDGE_MARGIN - span / 2;
  const highest = range.lo - EDGE_MARGIN + span / 2;
  if (lowest <= highest) {
    return Math.min(highest, Math.max(lowest, snapped));
  }
  // 여백까지 챙길 자리가 없다 — `MAX_SPAN`에 걸린 테이크가 여기 온다. 그래도 **담기는**
  // 한다: 여백 없이 한 번 더 물려서 한쪽 끝이 잘리고 반대쪽이 비는 일을 막는다.
  const tightLow = range.hi - span / 2;
  const tightHigh = range.lo + span / 2;
  return tightLow > tightHigh
    ? snapped
    : Math.min(tightHigh, Math.max(tightLow, snapped));
};

/**
 * 부른 폭을 감싸는 창. `sung`이 테이크 전체 폭(롤링 창이 아님)이라 창은 테이크 안에서
 * 넓어지기만 한다 — 고음을 찍고 내려와도 축이 되돌아오지 않는다.
 *
 * `mustInclude`는 **아직 안 불렀지만 화면에 있어야 하는 범위**다(노래 따라부르기의 가이드
 * 멜로디). 부른 폭만 보고 창을 잡으면 가이드의 높은 음이 화면 위로 잘려 나가, 따라갈 목표를
 * 못 보게 된다.
 *
 * **부른 폭을 빼고 가이드만으로 잡지 않는다.** 그러면 오검출에 안 흔들려 좋아 보이지만,
 * 멜로디를 옥타브 올려 부르는 사람(이 차트는 C#3~C4라 여성에게는 정상 반응이다)의 목소리가
 * 12반음 중 7개까지 가장자리에 뭉개진다. 오검출은 `MAX_SPAN`이 막는 몫이고, 그것만으로도
 * 실측 입력에서 멜로디가 화면 높이의 18.3% → 30.6%로 돌아온다.
 */
export const relativeWindow = (
  sung: SungRange | null,
  mustInclude: SungRange | null = null,
): MidiWindow => {
  const range = merge(sung, mustInclude);
  if (range == null) {
    return { lo: BASE_CENTER - MIN_SPAN / 2, hi: BASE_CENTER + MIN_SPAN / 2 };
  }
  let span = Math.min(
    MAX_SPAN,
    Math.max(MIN_SPAN, Math.ceil((range.hi - range.lo) / 12) * 12),
  );
  let center = centerFor(range, span);
  // 넓히는 것은 **실제로 안 들어갈 때뿐**이다. 예전 규칙은 올림한 옥타브에 여유 한
  // 옥타브를 늘 더했고, 그래서 낮게 부른 테이크가 제 폭의 두 배짜리 창 아래쪽 구석에
  // 눌렸다 — 맞게 부른 사람이 화면에서는 작아 보였다(2026-09-21 실기기).
  if (!fits(range, center, span) && span < MAX_SPAN) {
    span = Math.min(MAX_SPAN, span + 12);
    center = centerFor(range, span);
  }
  return { lo: center - span / 2, hi: center + span / 2 };
};

/**
 * midi → 세로 좌표(0 = 맨 위). `height`를 1로 주면 0..1 비율이 나온다 — SVG는 view 단위로,
 * CSS는 비율로 같은 함수를 쓴다.
 */
export const yOfWindow =
  ({ lo, hi }: MidiWindow, height: number) =>
  (midi: number): number =>
    height * (1 - (Math.min(hi, Math.max(lo, midi)) - lo) / (hi - lo));
